import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { AUDIT_HUB_USE_MOCK, AUDIT_STATUSES } from "./constants";
import {
  fetchAudits,
  requestAuditReport,
  resetAuditReport,
} from "../../../store/auditHub/auditHubSlice";
import {
  isApproachingCompletion,
  isStaleAudit,
} from "./utils";
import {
  AH_MUTED,
  AH_TEXT,
  AuditHubThemeProvider,
  auditHubPageSx,
  primaryButtonSx,
  searchFieldSx,
  tableShellSx,
  toolbarRowSx,
} from "./theme";
import SummaryCards from "./components/SummaryCards";
import FilterMenu from "./components/FilterMenu";
import {
  ActiveAuditsTable,
  CompletedAuditsTable,
  HubEmptyState,
} from "./components/AuditsTable";

const matchesQuery = (audit, query) => {
  if (!query) return true;
  const hay = [audit.auditCode, audit.auditId, audit.name, audit.ownerEmail, audit.status]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(query);
};

const AuditHub = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const list = useSelector((s) => s.auditHub?.list) || { items: [], status: "idle", error: null };
  const report = useSelector((s) => s.auditHub?.report) || {};
  const [query, setQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    document.title = "Audit Hub";
  }, []);

  useEffect(() => {
    dispatch(fetchAudits({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    const failures = history.location.state?.scanDispatchFailures;
    if (!Array.isArray(failures) || !failures.length) return;
    setSnackbar({
      open: true,
      message: `Audit created. Scan dispatch: ${failures.join(" ")}`,
      severity: "warning",
    });
    history.replace({ pathname: history.location.pathname, state: undefined });
  }, [history]);

  useEffect(() => {
    if (report.status === "succeeded" && report.downloadUrl) {
      window.open(report.downloadUrl, "_blank", "noopener,noreferrer");
      setSnackbar({ open: true, message: "Report is ready.", severity: "success" });
      dispatch(resetAuditReport());
    } else if (report.status === "failed" && report.error) {
      setSnackbar({ open: true, message: report.error, severity: "error" });
      dispatch(resetAuditReport());
    }
  }, [report.status, report.downloadUrl, report.error, dispatch]);

  const audits = list.items || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return audits.filter((audit) => {
      if (!matchesQuery(audit, q)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(audit.status)) return false;
      return true;
    });
  }, [audits, query, selectedStatuses]);

  const activeRows = filtered.filter((a) => a.status === AUDIT_STATUSES.ACTIVE);
  const completedRows = filtered.filter(
    (a) => a.status === AUDIT_STATUSES.COMPLETED || a.status === AUDIT_STATUSES.CANCELLED
  );

  const summarySource = useMemo(() => {
    const q = query.trim().toLowerCase();
    return audits.filter((audit) => matchesQuery(audit, q));
  }, [audits, query]);

  const counts = useMemo(() => {
    const active = summarySource.filter((a) => a.status === AUDIT_STATUSES.ACTIVE);
    return {
      inProgress: active.length,
      approaching: active.filter(isApproachingCompletion).length,
      stale: active.filter(isStaleAudit).length,
    };
  }, [summarySource]);

  const goCreate = () => history.push("/audit-hub/create");
  const handleDownload = (audit) => {
    dispatch(requestAuditReport({ auditId: audit.auditId, format: "docx" }));
    setSnackbar({ open: true, message: `Generating report for ${audit.auditCode}…`, severity: "info" });
  };
  const handleOpenAudit = () => history.push("/compliance/");

  const loading = list.status === "loading" || list.status === "idle";
  const hasAny = audits.length > 0;
  const truncated = Number(list.total) > audits.length;

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Audit Hub
              </Typography>
              <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                Create and track manual-control audits.
                {AUDIT_HUB_USE_MOCK
                  ? " Using the local OpenAPI mock until the service is deployed."
                  : ""}
              </Typography>
            </Box>

            <Box sx={tableShellSx}>
              <Box sx={{ ...toolbarRowSx, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search audits..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...searchFieldSx,
                    flex: "245 1 0%",
                    minWidth: 160,
                    maxWidth: "100%",
                  }}
                />
                <Box sx={{ flex: "276 1 0%", minWidth: 0 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    gap: 1.25,
                    flexShrink: 0,
                  }}
                >
                  <FilterMenu
                    anchorEl={filterAnchor}
                    onOpen={(e) => setFilterAnchor(e.currentTarget)}
                    onClose={() => setFilterAnchor(null)}
                    selectedStatuses={selectedStatuses}
                    onToggleStatus={(status) => {
                      setSelectedStatuses((prev) =>
                        prev.includes(status) ? prev.filter((x) => x !== status) : [...prev, status]
                      );
                    }}
                    onClear={() => setSelectedStatuses([])}
                  />
                  <Button
                    startIcon={<AddIcon />}
                    onClick={goCreate}
                    sx={{ ...primaryButtonSx, flexShrink: 0 }}
                  >
                    Create New Audit
                  </Button>
                </Box>
              </Box>

              {list.error ? (
                <Alert severity="error" sx={{ borderRadius: "10px" }}>
                  {list.error}
                </Alert>
              ) : loading && !hasAny ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !hasAny ? (
                <HubEmptyState
                  onCreate={
                    <Button startIcon={<AddIcon />} onClick={goCreate} sx={primaryButtonSx}>
                      Create New Audit
                    </Button>
                  }
                />
              ) : (
                <>
                  {truncated ? (
                    <Alert severity="info" sx={{ borderRadius: "10px", mb: 2 }}>
                      Showing {audits.length} of {list.total} audits.
                    </Alert>
                  ) : null}
                  <SummaryCards counts={counts} />
                  <ActiveAuditsTable
                    rows={activeRows}
                    onOpenAudit={handleOpenAudit}
                    onDownload={handleDownload}
                  />
                  <CompletedAuditsTable rows={completedRows} onDownload={handleDownload} />
                </>
              )}
            </Box>
          </Box>
        </Container>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuditHubThemeProvider>
  );
};

export default AuditHub;
